import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, from, merge, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, map, scan, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { IReporteVigilancia } from './reporte-vigilancia';

const API_URL = environment.apiUrl;

@Injectable()
export class ReporteVigilanciaService {

  private dateSelectedSubject = new BehaviorSubject<string>('');
  private studentsUrl = `${API_URL}/api/Asistencia/reporte/tarea/fecha/${moment().format('YYYY-MM-DD')}`;
  dateSelectedAction$ = this.dateSelectedSubject.asObservable()
    .pipe(
      map(data => {
        this.studentsUrl = `${API_URL}/api/Asistencia/reporte/tarea/fecha/${data}`;
        this.guardsUrl = `${API_URL}/api/Asistencia/tarea/fecha/${data}/cuidador`;
        this.canNotifyUrl = `${API_URL}/api/asistencia/notificar/tarea/fecha/${data}`;
        this.selectedDate = data;
      })
    );
  selectedDate = moment().format('YYYY-MM-DD');

  studentList$ = this.dateSelectedAction$.pipe(
    concatMap(date =>
      this.http.get<IReporteVigilancia[]>(this.studentsUrl)
        .pipe(
          catchError(err => {
            return throwError(err);
          })
        )
    ),
    catchError(this.handleError)
  );

  private teacherInsertedSubject = new Subject<any>();
  teacherInsertedAction$ = this.teacherInsertedSubject.asObservable();
  private guardsUrl = `${API_URL}/api/Asistencia/tarea/fecha/${this.selectedDate}/cuidador`;
  guardingTeachers$ = this.dateSelectedAction$
    .pipe(
      concatMap(date =>
        this.http.get(this.guardsUrl)
          .pipe(
            catchError(err => throwError(err))
          )
      )
    );

  guardingTeachersOp$ = merge(
    this.guardingTeachers$,
    this.teacherInsertedAction$
  )
    .pipe(
      scan((acc, value) => {
        if (value.op === 'remove') {
          return acc.filter(teacherI => teacherI.id !== value.teacher.id);
        }
        if (value.op === 'add') {
          return [...acc, value.teacher];
        }
      }),
      map(data => data.sort((a, b) => this.sortAlphabetically(a, b))
      )
    );

  canNotifyUrl = `${API_URL}/api/asistencia/notificar/tarea/fecha/${moment().format('YYYY-MM-DD')}`;
  canNotify$ = this.dateSelectedAction$.pipe(
    concatMap(date =>
      this.http.get(this.canNotifyUrl)
        .pipe(
          catchError(err => {
            return throwError(err);
          })
        )
    ),
    catchError(this.handleError)
  );

  constructor(private http: HttpClient) {
  }

  addVigilante(teacher) {
    const model = {
      fecha: this.selectedDate,
      cuidadores: [
        teacher
      ]
    };
    return this.http.post(`${API_URL}/api/asistencia/tarea/cuidador`, model).pipe(
      map(data => {
        this.teacherInsertedSubject.next({ op: 'add', teacher });
      }),
      catchError(this.handleError)
    );

  }

  removeVigilante(teacher) {
    return this.http.delete(`${API_URL}/api/asistencia/tarea/fecha/${this.selectedDate}/cuidador/${teacher.id}`).pipe(
      map(data => {
        this.teacherInsertedSubject.next({ op: 'remove', teacher });
      }),
      catchError(this.handleError)
    );
  }

  selectedDateChange(selectedDate: string): void {
    this.studentsUrl = `${API_URL}/api/Asistencia/reporte/tarea/fecha/${selectedDate}`;
    this.guardsUrl = `${API_URL}/api/Asistencia/tarea/fecha/${selectedDate}/cuidador`;
    this.dateSelectedSubject.next(selectedDate);
  }

  notificar() {
    return this.http.get(`${API_URL}/api/Asistencia/notificar/tarea/fecha/${this.selectedDate}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  private sortAlphabetically(a, b) {
    const textA = a.apellidosNombres.toUpperCase(),
      textB = b.apellidosNombres.toUpperCase();
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
  }

  private handleError(err: any) {
    // in a real world app, we may send the server to some remote logging infrastructure
    // instead of just logging it to the console
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = `Backend returned code ${err.status}(${err.statusText}): ${err.error}`;
    }
    return throwError(errorMessage);
  }
}
